import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    calculatePuzzleLevel,
    extractSeasonFromRow,
    isLevelLocked,
    SEASON_MAX_LEVEL,
} from "../wordWheelLevelUtils";

export default function WordWheelSetLevelDialog({
    open,
    onClose,
    onConfirm,
    templates = [],
    loading = false,
}) {
    const editableTemplates = useMemo(
        () => templates.filter((row) => !isLevelLocked(row)),
        [templates]
    );
    const lockedCount = templates.length - editableTemplates.length;
    const previewTemplate = editableTemplates[0] || templates[0] || null;
    const calculatedLevel = previewTemplate ? calculatePuzzleLevel(previewTemplate) : null;

    const [manualLevel, setManualLevel] = useState("");

    useEffect(() => {
        if (open && calculatedLevel != null) {
            setManualLevel(String(calculatedLevel));
        }
    }, [open, calculatedLevel]);

    const handleConfirm = () => {
        const parsed = parseInt(manualLevel, 10);
        if (!Number.isFinite(parsed) || parsed < 1 || parsed > SEASON_MAX_LEVEL) {
            return;
        }
        onConfirm({
            puzzleLevel: parsed,
            templates: editableTemplates,
        });
    };

    const parsedLevel = parseInt(manualLevel, 10);
    const levelValid = Number.isFinite(parsedLevel) && parsedLevel >= 1 && parsedLevel <= SEASON_MAX_LEVEL;

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Set Puzzle Level</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Assign level (1 = easiest, {SEASON_MAX_LEVEL} = hardest) to {editableTemplates.length} selected template(s).
                    </Typography>

                    {lockedCount > 0 && (
                        <Alert severity="info">
                            {lockedCount} selected template(s) have locked levels and will be skipped.
                        </Alert>
                    )}

                    {editableTemplates.length === 0 ? (
                        <Alert severity="warning">
                            All selected templates have locked levels. Choose puzzles from a season that is still
                            building its catalog, or puzzles without a locked level.
                        </Alert>
                    ) : (
                        <>
                            {previewTemplate && extractSeasonFromRow(previewTemplate) && (
                                <Typography variant="caption" color="text.secondary">
                                    Season: {extractSeasonFromRow(previewTemplate)}
                                </Typography>
                            )}
                            <TextField
                                label={`Puzzle level (1–${SEASON_MAX_LEVEL})`}
                                size="small"
                                type="number"
                                fullWidth
                                value={manualLevel}
                                onChange={(e) => setManualLevel(e.target.value)}
                                disabled={loading}
                                slotProps={{
                                    htmlInput: { min: 1, max: SEASON_MAX_LEVEL, step: 1 },
                                }}
                                helperText={`Calculated suggestion: ${calculatedLevel ?? "—"}`}
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={loading || editableTemplates.length === 0 || !levelValid}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Set Level
                </Button>
            </DialogActions>
        </Dialog>
    );
}
